/**
 * The catalog metadata columns the browse search blocks' row template
 * (TableListing, "cards-id") actually reads -- for all four browse families
 * (documents, delegations, glossary, people) across all three sites.
 *
 * WHY THIS EXISTS (item 8, 2026-09-27)
 *
 * Both the SSR seed (components/Browse/getSearchBlockAsyncData) and the
 * client mount (customizations/.../Listing/withQuerystringResults) sent
 * `metadata_fields: '_all'` on every browse container's first-page request.
 * plone.restapi's DefaultJSONSummarySerializer expands `_all` to the WHOLE
 * catalog schema (plone/restapi/serializer/summary.py:116-122) on every one
 * of the 25 rows a page renders, most of which no browse row template ever
 * reads. Measured 2026-09-27 (POST @querystring-search against each site's
 * real, stored `/documents` block query -- portal_type criterion, b_size 25,
 * each site's own sort_on/sort_order -- against 127.0.0.1:9080 on the VM):
 * directives 92,927 -> 33,385 bytes (-64.1%), standards 113,661 -> 43,380
 * bytes (-61.8%), nnsa 103,711 -> 37,434 bytes (-63.9%). `items_total` and
 * row count were identical between the `_all` and narrowed requests in every
 * case (1802/25, 597/25, 387/25), confirming the saving is purely per-row
 * column weight, not a change in which rows matched.
 *
 * The list below is the UNION of every field named in the 12 real browse
 * blocks' `fields`/`facets` (4 families x standards/directives/nnsa -- one
 * site is missing each of delegations/glossary/people; dumped straight from
 * the backend REST API, 2026-09-27) plus the two fields the row template
 * reads unconditionally, regardless of which columns a block configures:
 *
 *   - `getObjSize` gates the docid column's "PDF" link (TableListing.jsx,
 *     `hasFile`) -- without it every document row acts fileless.
 *   - `UID` is the row `key` fallback when `@id` is absent
 *     (`item['@id'] || item.UID || idx`).
 *
 * `Title` (capitalized) is a DIFFERENT key from the default summary's
 * lowercase `title`: plone.restapi's DefaultJSONSummarySerializer aliases
 * `title` -> the `Title` catalog column but stores the result under the
 * lowercase key (serializer/summary.py `field_accessors`), while the row
 * template reads `item.Title`. Dropping the capitalized form from this list
 * would blank the title column even though `item.title` still resolved.
 *
 * `effective` (the un-repointed date name blocks are still stored with) is
 * deliberately NOT in this list: searchMap.ts's `normalizeBlockData` repoints
 * every single-field date column onto `issue_date`, `authority_effective_date`
 * or -- only for a family it has not measured -- the `effective` fallback.
 * Every family actually seen in the corpus resolves to one of the first two
 * (documents: Standard/Directive+OtherPolicy/Policy are all in
 * ISSUE_DATE_TYPES; delegations: Delegation only), so `effective` is covered
 * only as insurance against a future family that mixes types and falls
 * through to it -- add it back here first if that ever happens, rather than
 * as an emergency fix once a date column goes blank.
 *
 * This is a SINGLE shared list, not one per family or site: both call sites
 * send the identical array, so `helpers/querystringKey.js` (whose key
 * includes `metadata_fields`) produces the SAME key for the SSR seed and the
 * client mount no matter which family or site the block belongs to. A list
 * that varied by family would make the two sides agree only by accident --
 * see querystringKey.js's own header for what silently reappearing duplicate
 * requests looks like.
 *
 * Only the BROWSE family blocks (`family` truthy on the block's own stored
 * data -- the four canonical containers and their `?preset=` pages) use this
 * list. Every other search block -- the Search App (`config/searchApp.ts`, 9
 * mixed types, no `family` stamp, needs its own much wider column set) and any
 * plain listing block (news listings, `variation.fullobjects`) -- keeps
 * `metadata_fields: '_all'`; gating is at the two call sites, not here. The
 * two sites read `family` off DIFFERENT props, because they sit at different
 * points in the data flow: the SSR seed (getSearchBlockAsyncData.js) is
 * handed the block's raw stored data directly, so it reads `data?.family`.
 * The client mount (withQuerystringResults.jsx) sits inside ListingBody,
 * downstream of SearchBlockView's `withControls(applyDefaults(...))`, which
 * rebuilds its `data` prop from scratch and drops `family` -- but
 * SearchBlockView also passes `variation={{ ...data, ...listingBodyVariation }}`,
 * spreading the whole block data into `variation` first, so the client reads
 * `variation?.family` (the same object `variation?.fullobjects` already read
 * a block-level flag from, right next to this gate).
 */
export const BROWSE_METADATA_FIELDS = [
  // title / identity
  'Title',
  'UID',
  // documents + delegations: the document-number column and its file link
  'docid',
  'getObjSize',
  // documents + delegations: the status pill
  'status',
  // documents family (standards/directives/nnsa): the OPI column
  'opi',
  // documents family: the repointed date column (searchMap.ts EFF, issue_date
  // branch -- Standard/Directive/OtherPolicy/Policy are all ISSUE_DATE_TYPES)
  'issue_date',
  // delegations family (directives/nnsa): the repointed date column
  // (searchMap.ts EFF, authority_effective_date branch)
  'authority_effective_date',
  // delegations family: the delegant/delegate columns
  'delegant',
  'delegate',
  // glossary family (directives): the type + OPI columns
  'portal_type',
  'term_opi',
  // people family (standards): the role + status columns
  'person_role',
  'person_status',
];

export default BROWSE_METADATA_FIELDS;
